import {defineStore} from 'pinia'
import {ref} from 'vue'
import type {WebAppInitData} from 'telegram-web-app'

interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
  auth_date: number
  hash: string
}

export const useTelegramStore = defineStore(
  'telegram',
  () => {
    const initData = ref('')
    const initDataUnsafe = ref<WebAppInitData>()
    const isReady = ref(false)
    const isTelegramWebApp = ref(false)
    const browserAuthData = ref<TelegramUser | null>(null)
    const startParam = ref<string | null>(null)

    const initialize = () => {
      // Проверяем, действительно ли запущено в Telegram WebApp
      // Используем несколько методов проверки:
      const isRealWebApp = () => {
        // 1. Проверяем initData (основной способ)
        const hasInitData = window.Telegram?.WebApp?.initData

        // 2. Проверяем localStorage для отладки
        const hasStoredInitData = localStorage.getItem('initData')

        // 3. Проверяем User Agent на наличие Telegram
        const isTelegramUA = navigator.userAgent.includes('Telegram')

        // 4. Проверяем специфичные для WebApp методы
        const hasWebAppMethods =
          window.Telegram?.WebApp?.ready !== undefined && window.Telegram?.WebApp?.expand !== undefined

        return (hasInitData || hasStoredInitData) && (isTelegramUA || hasWebAppMethods)
      }

      if (isRealWebApp()) {
        console.log('Detected real Telegram WebApp context')
        isTelegramWebApp.value = true
        initData.value = window.Telegram!.WebApp!.initData

        if (!initData.value) {
          // TODO: remove this, it's for testing
          initData.value = localStorage.getItem('initData') || ''
        }

        initDataUnsafe.value = window.Telegram!.WebApp!.initDataUnsafe
        startParam.value = window.Telegram?.WebApp?.initDataUnsafe?.start_param ?? null
        isReady.value = true
      } else {
        console.log('Detected browser context, will show login widget')
        // Если нет данных WebApp, проверяем есть ли сохраненные данные браузера
        isTelegramWebApp.value = false
        const savedAuthData = localStorage.getItem('telegramBrowserAuth')
        if (savedAuthData) {
          try {
            browserAuthData.value = JSON.parse(savedAuthData)
            isReady.value = true
            generateInitDataFromBrowserAuth()
          } catch (error) {
            console.error('Failed to parse saved auth data:', error)
          }
        }
      }
    }

    const handleBrowserAuth = (userData: TelegramUser) => {
      console.log('📝 handleBrowserAuth called with:', userData)

      browserAuthData.value = userData
      localStorage.setItem('telegramBrowserAuth', JSON.stringify(userData))

      console.log('💾 Browser auth data saved to localStorage')

      // Создаем initDataUnsafe для UI, но не имитируем initData
      generateInitDataFromBrowserAuth()
      isReady.value = true

      console.log('✅ Browser auth completed, isReady:', isReady.value)
    }

    const generateInitDataFromBrowserAuth = () => {
      if (!browserAuthData.value) {
        console.log('❌ No browserAuthData available')
        return
      }

      console.log('🔧 Generating initDataUnsafe from browser auth...')

      // Создаем только initDataUnsafe объект для отображения в UI
      initDataUnsafe.value = {
        user: {
          id: browserAuthData.value.id,
          first_name: browserAuthData.value.first_name,
          last_name: browserAuthData.value.last_name,
          username: browserAuthData.value.username,
          photo_url: browserAuthData.value.photo_url,
          language_code: 'ru',
        },
        auth_date: browserAuthData.value.auth_date,
        hash: browserAuthData.value.hash,
      }

      console.log('👤 Generated initDataUnsafe:', initDataUnsafe.value)

      // НЕ генерируем initData - оставляем пустым для браузерной авторизации
      initData.value = ''
    }

    const logout = () => {
      initData.value = ''
      initDataUnsafe.value = undefined
      browserAuthData.value = null
      isReady.value = false
      localStorage.removeItem('telegramBrowserAuth')
      localStorage.removeItem('initData')
    }

    const isAuthenticated = () => {
      return isReady.value && (initData.value || browserAuthData.value)
    }

    return {
      initData,
      initDataUnsafe,
      isReady,
      isTelegramWebApp,
      browserAuthData,
      startParam,
      initialize,
      handleBrowserAuth,
      logout,
      isAuthenticated,
    }
  },
  {
    persist: true,
  }
)
